import React, { memo, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import isEqual from 'lodash/isEqual'
import { UNASSIGNED_VALUE } from 'transfix-ui/components/Autosuggest'
import { Button } from 'transfix-ui/components/Button'
import { InlineMessage } from 'transfix-ui/components/InlineMessage'
import { useValidationFormContext } from 'utils/context/ValidationFormContext'
import { useRefresh } from 'utils/hooks/useRefresh'
import { DriverAutosuggest } from 'apps/fleet-planner/components/atoms/DriverAutosuggest'
import { DriverForm } from 'apps/fleet-planner/components/forms/DriverForm'
import { AddEntityModal } from 'apps/fleet-planner/components/modals/AddEntityModal'
import { FP_SUPPORT_EMAIL } from 'apps/fleet-planner/config'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useTeamMembersApi } from 'apps/fleet-planner/hooks/useTeamMembersApi'
import { DriverSchemaType } from 'apps/fleet-planner/schema'
import {
  IDriver,
  ILoad,
  ITeamMember,
  TEAM_MEMBER_ROLE_TYPES,
  TEAM_MEMBER_USER_TYPES,
} from 'apps/fleet-planner/types'
import { fetchDriversSuccess } from 'apps/fleet-planner/utils/actions/drivers'
import * as Api from 'apps/fleet-planner/utils/api'
import { getNamesFromInput } from 'apps/fleet-planner/utils/getNamesFromInput'
import { selectActiveDrivers } from 'apps/fleet-planner/utils/selectors'
import { Toast, useToastState } from 'transfix-ui'

const getDefaultValues = (driver: IDriver | undefined | null) => {
  return {
    firstName: driver?.firstName ?? '',
    lastName: driver?.lastName ?? '',
    cellPhone: driver?.cellPhone ?? '',
    motorCarrier: driver?.motorCarrier ?? undefined,
    email: driver?.email ?? '',
  }
}

interface IDriverSingleProps {
  value: IDriver | undefined | null
  isRemoveButtonVisible?: boolean
  isAddButtonVisible?: boolean
  selectedDrivers: ILoad['drivers']
  onAddDriver: () => void
  onChange: (change: IDriver | undefined | null) => void
  onValidationChange?: (message?: string) => void
}

export const DriverSingle = memo<IDriverSingleProps>(
  ({
    value: valueProp,
    onChange: onChangeProp,
    onAddDriver: onAddDriverProp,
    selectedDrivers: selectedDriversProp = [],
    onValidationChange: onValidationChangeProp,
  }) => {
    const { trackEvent } = useFMSAnalytics()
    const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()
    const dispatch = useDispatch()
    const drivers = useSelector(selectActiveDrivers)
    const { createTeamMember } = useTeamMembersApi()
    const [hasAccountConflict, setHasAccountConflict] = useState<boolean>(false)

    const {
      errors: driverFormErrors,
      setHasErrors: setHasDriverFormErrors,
    } = useValidationFormContext()

    const [isDriverFormVisible, setDriverFormVisible] = useState<boolean>(false)
    const [currentDriver, setCurrentDriver] = useState<IDriver | undefined | null>(valueProp)

    // Used for adding default name via input "add new __"
    const [defaultValues, setDefaultValues] = useState<IDriver | undefined>(
      getDefaultValues(valueProp)
    )

    const { key: autocompleteKey, resetKey } = useRefresh(currentDriver?.id)

    // When value updates, also update local state
    useEffect(() => {
      setDefaultValues(getDefaultValues(valueProp))
    }, [valueProp])

    // Update driver props
    useEffect(() => {
      if (!isEqual(valueProp, currentDriver)) {
        setCurrentDriver(valueProp)
      }
    }, [currentDriver, resetKey, valueProp])

    const driverChangeHandler = useCallback(
      (selectedDriver?: IDriver | undefined | null) => {
        onChangeProp(selectedDriver)
      },
      [onChangeProp]
    )

    const cancelAddNewDriverHandler = () => {
      setDriverFormVisible(false)
      setDefaultValues({
        firstName: '',
        lastName: '',
        email: '',
        cellPhone: '',
      })
      resetKey()
      setHasDriverFormErrors(false)
    }

    const addEntityClickHandler = () => setDriverFormVisible(true)

    /**
     * Create a new driver using form input
     */
    const addNewDriverHandler = useCallback(
      async (d: DriverSchemaType) => {
        const payload: RecursivePartial<ITeamMember> = {
          firstName: d.firstName,
          lastName: d.lastName,
          cellPhone: d.cellPhone,
          phone: d.phone,
          email: d.email,
          smsStatusUpdatesEnabled: !!d.settings?.smsStatusUpdatesEnabled,
          motorCarrierDetails: [
            {
              role: TEAM_MEMBER_ROLE_TYPES.EMPLOYEE,
              userType: TEAM_MEMBER_USER_TYPES.DRIVER,
              motorCarrierId: d.motorCarrierId,
            },
          ],
        }
        let createdTeamMember: ITeamMember | undefined
        setHasAccountConflict(false)
        try {
          createdTeamMember = await createTeamMember(payload)
          const { drivers: fetchedDrivers = [] } = (await Api.fetchAllDrivers()) || {}
          const driver = fetchedDrivers.find(
            fetchedDriver => fetchedDriver.transfixUserId === createdTeamMember?.id
          )
          // Send update to parent
          driverChangeHandler(driver)
          // Update redux
          dispatch(fetchDriversSuccess(fetchedDrivers))
          // Close form
          setDriverFormVisible(false)
          setHasDriverFormErrors(false)
          // Reset autocomplete
          resetKey()
        } catch (e) {
          if (e === 'Error: email and cellphone match different existing accounts') {
            setHasAccountConflict(true)
          }
        }
        if (createdTeamMember) {
          const { firstName = '', lastName = '' } = createdTeamMember || {}
          const fullNameString = firstName && lastName ? ` (“${firstName} ${lastName}”)` : ''
          openToast({
            toastType: 'default',
            toastMessage: createdTeamMember.isExternal
              ? `Your driver already has a Transfix account${fullNameString}. They’ve been added to your carrier.`
              : 'Driver added.',
          })
        }
      },
      [createTeamMember, dispatch, driverChangeHandler, resetKey, setHasDriverFormErrors]
    )

    const setDriverFormHandler = useCallback(
      (isFormVisible: boolean, formIndex: number, name?: string | undefined) => {
        const { firstName, lastName } = getNamesFromInput(name)
        setDefaultValues(prev => ({
          ...prev,
          firstName,
          lastName,
          email: '',
          cellPhone: '',
        }))
        setDriverFormVisible(isFormVisible)
      },
      []
    )

    const driversBulkUploadClickHandler = () =>
      trackEvent('Bulk Upload', 'CLICK_DRIVERS_BULK_UPLOAD_LINK')

    return (
      <div data-test='load-driver'>
        <div className=''>
          <div className='flex items-start'>
            <div className='flex-auto'>
              <DriverAutosuggest
                key={autocompleteKey}
                showUnassignedSuggestion
                label={drivers?.length > 1 ? 'Driver 1' : 'Driver'}
                currentDrivers={selectedDriversProp}
                defaultValue={valueProp || UNASSIGNED_VALUE}
                onChange={driverChangeHandler}
                large
                formIndex={1}
                shouldFetchList={false}
                setDriverForm={setDriverFormHandler}
                onAddAnotherDriver={onAddDriverProp}
                isRemoveButtonVisible={false}
                hideAllButtons
                onValidationChange={onValidationChangeProp}
              />
              {!currentDriver?.id && (
                <Button
                  className='btn-text-link-b mt1 mb2'
                  data-test='add-new-truck-cta'
                  onClick={addEntityClickHandler}>
                  + Add new driver
                </Button>
              )}
            </div>
          </div>
        </div>
        {isDriverFormVisible && (
          <AddEntityModal
            open
            title='Add New Driver'
            error={driverFormErrors}
            onClose={cancelAddNewDriverHandler}>
            <InlineMessage
              className='mb5'
              message={
                <span>
                  <b>Lots of drivers to add?</b>{' '}
                  <a href={`mailto:${FP_SUPPORT_EMAIL}`} onClick={driversBulkUploadClickHandler}>
                    Contact us
                  </a>{' '}
                  and we’ll import your drivers in bulk.
                </span>
              }
            />
            <DriverForm
              defaultValues={defaultValues}
              onSuccess={addNewDriverHandler}
              onCancel={cancelAddNewDriverHandler}
              hasAccountConflict={hasAccountConflict}
            />
          </AddEntityModal>
        )}
        <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
      </div>
    )
  }
)
